/**
 * Deriving `CanonicalChord` from `ChordSpec`, and re-spelling a chord for a key context.
 *
 * `toCanonical` is the only place pitch classes are computed from structure, and
 * `respell` is the only place letters are chosen from pitch classes. Keeping both
 * funnels narrow is what makes "the recogniser hears sets, the session names them"
 * true rather than aspirational.
 */

import {
  degreeLetter,
  keyCenterLof,
  mod12,
  noteName,
  rankSpellings,
  spellOnLetter,
  spellPitchClass,
  spelledNoteToPc,
  type KeyContext,
} from './enharmonic.js';
import { formatChord, type FormatOptions } from './format.js';
import { isParseError, parseChordSymbol, type ParseError } from './parse.js';
import { normalizePcSet } from './pcset.js';
import {
  DEGREE_LETTER_STEPS,
  EXTENSION_DEGREES,
  EXTENSION_SEMITONES,
  QUALITY_FIFTH_SEMITONES,
  QUALITY_THIRD_SEMITONES,
  SEVENTH_SEMITONES,
  type CanonicalChord,
  type ChordDegree,
  type ChordSpec,
  type PitchClass,
  type SpelledNote,
} from './types.js';

/**
 * Semitone offset above the root for every degree the chord actually contains.
 * Alterations rewrite degree 5; omissions remove degrees 3 or 5. Order matters:
 * an omitted fifth beats an altered one.
 */
export function chordDegreeSemitones(spec: ChordSpec): Map<ChordDegree, number> {
  const degrees = new Map<ChordDegree, number>();
  degrees.set(1, 0);

  const third = QUALITY_THIRD_SEMITONES[spec.quality];
  if (third !== null) degrees.set(3, third);
  degrees.set(5, QUALITY_FIFTH_SEMITONES[spec.quality]);

  if (spec.seventh) degrees.set(7, SEVENTH_SEMITONES[spec.seventh]);

  for (const extension of spec.extensions) {
    degrees.set(EXTENSION_DEGREES[extension], EXTENSION_SEMITONES[extension]);
  }
  for (const alteration of spec.alterations) {
    if (degrees.has(5)) degrees.set(5, alteration === 'b5' ? 6 : 8);
  }
  for (const omission of spec.omissions) {
    degrees.delete(omission === '3' ? 3 : 5);
  }

  return degrees;
}

/** Ascending degree order for stable iteration and display. */
const DEGREE_ORDER: readonly ChordDegree[] = [1, 3, 5, 6, 7, 9, 11, 13];

/** A chord degree together with the note that realises it. */
export interface SpelledDegree {
  degree: ChordDegree;
  note: SpelledNote;
  pitchClass: PitchClass;
}

/**
 * How many letter-steps above the root a degree sits, for this chord.
 *
 * `DEGREE_LETTER_STEPS` answers for a stack of thirds, and a suspension is the one place
 * that is not what degree 3 is. A suspended fourth is written on the fourth letter — Csus4
 * is C–F–G — and spelling it on the third letter gives C–E♯–G, which is the same three
 * sounds and not a chord anybody has ever written down. The same for sus2 and E𝄫.
 */
function degreeLetterSteps(degree: ChordDegree, quality: ChordSpec['quality']): number {
  if (degree === 3) {
    if (quality === 'sus2') return 1;
    if (quality === 'sus4') return 3;
  }
  return DEGREE_LETTER_STEPS[degree];
}

/**
 * Spell every degree of the chord as a stack of thirds above the given root spelling.
 * Returns `null` if any degree would need more than a double accidental — that is the
 * "illegal third-stack" test the enharmonic policy uses as a hard filter.
 */
export function spellDegreeStack(
  spec: ChordSpec,
  root: SpelledNote,
  degrees = chordDegreeSemitones(spec),
): SpelledDegree[] | null {
  const rootPc = spelledNoteToPc(root);
  const out: SpelledDegree[] = [];
  for (const degree of DEGREE_ORDER) {
    const semitone = degrees.get(degree);
    if (semitone === undefined) continue;
    const pitchClass = mod12(rootPc + semitone);
    if (degree === 1) {
      out.push({ degree, note: root, pitchClass });
      continue;
    }
    const letter = degreeLetter(root.letter, degreeLetterSteps(degree, spec.quality));
    const note = spellOnLetter(letter, pitchClass);
    if (!note) return null;
    out.push({ degree, note, pitchClass });
  }
  return out;
}

/** Total accidental cost of a stack, or `null` when the stack cannot be written. */
function stackCost(
  spec: ChordSpec,
  root: SpelledNote,
  degrees: Map<ChordDegree, number>,
): number | null {
  const stack = spellDegreeStack(spec, root, degrees);
  if (!stack) return null;
  return stack.reduce((sum, entry) => sum + Math.abs(entry.note.accidental), 0);
}

/** Derive the cached, pitch-class-bearing form of a chord. */
export function toCanonical(spec: ChordSpec): CanonicalChord {
  const rootPc = spelledNoteToPc(spec.root);
  const bassPc = spec.bass ? spelledNoteToPc(spec.bass) : rootPc;
  const degrees = chordDegreeSemitones(spec);

  const sounding: PitchClass[] = [];
  for (const semitone of degrees.values()) {
    sounding.push(mod12(rootPc + semitone));
  }
  // The bass is sounding even when it is not a chord tone (C/D), and the recogniser
  // will hear it, so it belongs in the matching set.
  sounding.push(bassPc);

  let inversion: CanonicalChord['inversion'] = 0;
  if (bassPc !== rootPc) {
    const thirdSemitone = degrees.get(3);
    const fifthSemitone = degrees.get(5);
    const seventhSemitone = degrees.get(7);
    if (thirdSemitone !== undefined && mod12(rootPc + thirdSemitone) === bassPc) inversion = 1;
    else if (fifthSemitone !== undefined && mod12(rootPc + fifthSemitone) === bassPc) inversion = 2;
    else if (seventhSemitone !== undefined && mod12(rootPc + seventhSemitone) === bassPc)
      inversion = 3;
  }

  return {
    ...spec,
    extensions: [...spec.extensions],
    alterations: [...spec.alterations],
    omissions: [...spec.omissions],
    pitchClasses: normalizePcSet(sounding),
    rootPc,
    bassPc,
    inversion,
    symbol: formatChord(spec),
  };
}

/** Strip the derived fields back off, e.g. before sending a chord to the LLM. */
export function toSpec(chord: CanonicalChord): ChordSpec {
  const spec: ChordSpec = {
    root: chord.root,
    quality: chord.quality,
    extensions: [...chord.extensions],
    alterations: [...chord.alterations],
    omissions: [...chord.omissions],
  };
  if (chord.seventh) spec.seventh = chord.seventh;
  if (chord.bass) spec.bass = chord.bass;
  return spec;
}

/**
 * Choose letters for a chord's pitch classes against a key context.
 *
 * The chord's structure (quality, seventh, extensions) and its pitch classes are
 * untouched — only `root.letter`/`root.accidental`, the bass spelling, and therefore
 * `symbol` change. See `enharmonic.ts` for the full policy; in short: minimise
 * line-of-fifths distance from the key signature's centre, reject spellings whose
 * stack of thirds cannot be written, and break ties toward fewer accidentals, then
 * toward the cheaper stack, then toward the sharp side.
 *
 * `key` is structural, so a full `KeyEstimate` may be passed directly.
 */
export function respell(chord: CanonicalChord, key: KeyContext): CanonicalChord {
  const centerLof = keyCenterLof(key);
  const base = toSpec(chord);
  const degrees = chordDegreeSemitones(base);

  const ranked = rankSpellings(chord.rootPc, centerLof, (candidate) =>
    stackCost(base, candidate, degrees),
  );
  const root = ranked[0] as SpelledNote;

  const spec: ChordSpec = { ...base, root };
  delete spec.bass;

  if (chord.bassPc !== chord.rootPc) {
    // Prefer the spelling the chord already gives that pitch class — a C minor seventh
    // over its third must read Cm7/E♭, never Cm7/D♯.
    const stack = spellDegreeStack(spec, root, degrees);
    const fromStack = stack?.find((entry) => entry.pitchClass === chord.bassPc);
    spec.bass = fromStack ? fromStack.note : spellPitchClass(chord.bassPc, key);
  }

  return toCanonical(spec);
}

/** Note names for every degree of the chord, low to high, e.g. ["C", "E♭", "G", "B♭"]. */
export function chordToneSpellings(
  chord: CanonicalChord,
  opts?: { ascii?: boolean },
): string[] | null {
  const spec = toSpec(chord);
  const stack = spellDegreeStack(spec, chord.root);
  if (!stack) return null;
  return stack.map((entry) => noteName(entry.note, opts));
}

/** Parse straight to a canonical chord. Convenience for manual entry and tests. */
export function parseCanonicalChord(input: string): CanonicalChord | ParseError {
  const spec = parseChordSymbol(input);
  return isParseError(spec) ? spec : toCanonical(spec);
}

/** Re-render a canonical chord's symbol with different options, without re-deriving it. */
export function formatCanonical(chord: CanonicalChord, opts?: FormatOptions): string {
  return formatChord(toSpec(chord), opts);
}
